
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  maxTocLevel: 2,
  description: 'Migrate from /accounts/balance/get to /signal/evaluate'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Balance migration guide' mdxType="Header" />
    <h4 {...{
      "id": "why-migrate"
    }}>{`Why migrate?`}</h4>
    <p>{`If you use Balance to reduce ACH return risk, migrating your integration from `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` and Signal Rules provides the following benefits:`}</p>
    <ul>
      <li parentName="ul">{`Configure and customize business logic for accepting transactions in the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/signal/"
        }}>{`Signal Dashboard`}</a>{`, without making any code changes.`}</li>
      <li parentName="ul">{`View your transaction return activity and performance via the Dashboard.`}</li>
      <li parentName="ul">{`Access `}<a parentName="li" {...{
          "href": "/docs/signal/tuning-rules/"
        }}>{`personalized recommendations and scenario modeling`}</a>{` to help tune and optimize your rules.`}</li>
      <li parentName="ul">{`If you need to upgrade certain balance checks to using Signal Transaction Scores for lower latency, deeper insights, or more sophisticated rules logic, you can do so without any code changes.`}</li>
    </ul>
    <p>{`Note: Signal Rules and `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` are only available for use cases involving evaluating potential ACH transactions for return risk. Other Balance use cases should continue to use `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "migration-steps"
    }}>{`Migration steps`}</h4>
    <Callout mdxType="Callout">
      <p>{`If you began using Balance prior to October 15, 2025 and would like to migrate to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`, contact your account manager to request access.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Configure `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/signal/risk-profiles"
          }}>{`Signal Rules`}</a>{` in the Dashboard to create a `}<strong parentName="p">{`Balance-only ruleset`}</strong>{` that matches your existing business logic. For more details, see `}<a parentName="p" {...{
            "href": "/docs/signal/signal-rules/"
          }}>{`Signal Rules`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Update any `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` calls to include `}<inlineCode parentName="p">{`signal`}</inlineCode>{` in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` array. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Replace your `}<inlineCode parentName="p">{`/accounts/balance/get`}</inlineCode>{` calls with calls to `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`. At a minimum, you will need to send the following parameters:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`The `}<inlineCode parentName="li">{`access_token`}</inlineCode></li>
          <li parentName="ul">{`The `}<inlineCode parentName="li">{`account_id`}</inlineCode>{` of the account you will be debiting`}</li>
          <li parentName="ul">{`The transaction `}<inlineCode parentName="li">{`amount`}</inlineCode></li>
          <li parentName="ul">{`A `}<inlineCode parentName="li">{`client_transaction_id`}</inlineCode>{` of your choosing to uniquely identify the proposed transaction`}</li>
          <li parentName="ul">{`The `}<inlineCode parentName="li">{`ruleset_key`}</inlineCode>{` you want to use, if you are using multiple rulesets (will default to using the `}<inlineCode parentName="li">{`default`}</inlineCode>{` ruleset if not specified)`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li"><inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{` will return a `}<inlineCode parentName="p">{`ruleset.result`}</inlineCode>{` such as `}<inlineCode parentName="p">{`ACCEPT`}</inlineCode>{` or `}<inlineCode parentName="p">{`REROUTE`}</inlineCode>{`, based on your ruleset logic. In your application's code, replace the balance-based logic around accepting or rerouting a proposed transaction to instead use the value of this field. `}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`If you still need direct access to real-time balance data (for example, to display in your UI) you can access it via the `}<inlineCode parentName="p">{`core_attributes.available_balance`}</inlineCode>{` and `}<inlineCode parentName="p">{`core_attributes.current_balance`}</inlineCode>{` fields returned by `}<inlineCode parentName="p">{`/signal/evaluate`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "/docs/signal/reporting-returns/"
          }}>{`Report whether you proceeded with the transaction`}</a>{` by calling `}<inlineCode parentName="p">{`/signal/decision/report`}</inlineCode>{`. You only need to do this if the result was `}<inlineCode parentName="p">{`REVIEW`}</inlineCode>{`, or if you did not follow the recommendation in the result (e.g. accepted a transaction even if the result was `}<inlineCode parentName="p">{`REROUTE`}</inlineCode>{`).`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`If you later receive an ACH return for an accepted transaction, call `}<inlineCode parentName="p">{`/signal/return/report`}</inlineCode>{`.`}</p>
      </li>
    </ol>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "why-migrate",
        level: 4,
        title: "Why migrate?",
      },
{
        id: "migration-steps",
        level: 4,
        title: "Migration steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
